// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A control component for managing asset properties.
 * This module provides a comprehensive interface for viewing
 * and modifying various asset properties including status,
 * ownership, location, and project assignment. It includes
 * a map visualization and supports dialog-based editing.
 *
 * Key features:
 * - Property management
 * - Status control
 * - Location control
 * - Project assignment
 * - Map visualization
 * - Dialog integration
 */
import { getEventGroupConfigurations } from '@/asset/api/get-event-group-configurations';
import { Asset } from '@/asset/api/types';
import { AssetAssignmentDialog } from '@/asset/components/asset-assignment-dialog';
import { AssetLocationControl } from '@/asset/components/asset-location-control';
import { AssetMap } from '@/asset/components/asset-map';
import { AssetOwnershipControl } from '@/asset/components/asset-ownership-control';
import { AssetUnassignmentDialog } from '@/asset/components/asset-unassignment-dialog';
import { EventGroupDialog } from '@/asset/components/event-group-dialog';
import { AssetFunctionalStatusControl } from '@/asset/components/functional-status-control';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, Stack, StackProps, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Props for configuring the AssetPropertiesControl component.
 * Extends MUI Stack props for styling flexibility.
 *
 * @interface Props
 */
interface Props extends Pick<StackProps, 'sx'> {
  /**
   * The asset whose properties are being managed.
   * Contains all asset details and relationships.
   */
  asset: Asset;
}

/**
 * A control component for comprehensive asset property management.
 * Provides an interface for viewing and modifying various asset
 * properties with support for dialog-based editing and map
 * visualization. Integrates multiple property-specific controls
 * into a unified interface.
 *
 * Features:
 * - Status management
 * - Ownership control
 * - Location control
 * - Project assignment
 * - Map visualization
 * - Dialog integration
 * - Responsive layout
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetPropertiesControl
 *       asset={asset}
 *       sx={{ padding: 2 }}
 *     />
 *   );
 * }
 * ```
 */
export function AssetPropertiesControl(props: Props) {
  // Dialog state management
  const locationDialog = useDisclosure();
  const assignmentDialog = useDisclosure();
  const unassignmentDialog = useDisclosure();

  // Fetch event group configurations
  const configurationsQuery = useSuspenseQuery({
    queryKey: ['event-group-configurations'],
    queryFn: getEventGroupConfigurations,
  });

  const configuration = configurationsQuery.data.configurations[0];

  /**
   * Handles project assignment button clicks.
   * Opens appropriate dialog based on current state.
   */
  const handleClickAssignment = () => {
    if (props.asset.project === null) {
      return assignmentDialog.open();
    }

    unassignmentDialog.open();
  };

  return (
    <>
      <AssetAssignmentDialog
        key={`assignment-${props.asset.project?.key}`}
        asset={props.asset}
        isOpen={assignmentDialog.isOpen}
        onClose={assignmentDialog.close}
      />
      <AssetUnassignmentDialog
        key={`unassignment-${props.asset.project?.key}`}
        asset={props.asset}
        isOpen={unassignmentDialog.isOpen}
        onClose={unassignmentDialog.close}
      />
      {configuration && (
        <EventGroupDialog
          asset={props.asset}
          isOpen={locationDialog.isOpen}
          onClose={locationDialog.close}
          configuration={configuration}
        />
      )}
      <Stack direction="row" justifyContent="end" spacing={1} sx={props.sx}>
        <Box
          marginLeft="auto"
          display="grid"
          gridTemplateColumns="auto 1fr"
          alignItems="center"
          alignContent="center"
          justifyItems="start"
          rowGap={0.5}
          columnGap={2}
        >
          <Typography variant="subtitle2">Status</Typography>
          <AssetFunctionalStatusControl asset={props.asset} />
          <Typography variant="subtitle2">Owner</Typography>
          <AssetOwnershipControl asset={props.asset} />
          <Typography variant="subtitle2">Location</Typography>
          <AssetLocationControl
            asset={props.asset}
            onClick={locationDialog.open}
          />
          <Typography variant="subtitle2">Project</Typography>
          <EditablePropertyButton onClick={handleClickAssignment}>
            <Typography
              variant="body2"
              color={
                props.asset.project === null ? 'text.secondary' : 'text.primary'
              }
            >
              {props.asset.project === null
                ? 'Unassigned'
                : props.asset.project.name}
            </Typography>
          </EditablePropertyButton>
        </Box>
        <AssetMap asset={props.asset} width="15rem" height="10rem" />
      </Stack>
    </>
  );
}
